import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCcw } from 'lucide-react';
import { logger } from '@/utils/logger';
import { posthogStartReplayOnError } from '@/services/posthog';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    logger.error('🔴 ErrorBoundary caught error:', error);
    return { hasError: true, error };
  }

  private isChunkLoadError(error: Error | undefined): boolean {
    if (!error) return false;
    return (
      error.message.includes('Failed to fetch dynamically imported module') ||
      error.message.includes('error loading dynamically imported module') ||
      error.message.includes('Importing a module script failed') ||
      error.message.includes('Loading chunk') ||
      error.name === 'ChunkLoadError'
    );
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('🔴 ErrorBoundary details:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      location: window.location.pathname
    });

    // Chunk-load error = stale cache after a new deploy → force reload once
    if (this.isChunkLoadError(error)) {
      const RELOAD_FLAG = '_octo_chunk_reload';
      if (!sessionStorage.getItem(RELOAD_FLAG)) {
        sessionStorage.setItem(RELOAD_FLAG, '1');
        window.location.reload();
        return;
      }
    }

    // Session replay PostHog sur erreur
    posthogStartReplayOnError();

    // Sentry
    import('@sentry/react').then((Sentry) => {
      Sentry.captureException(error, {
        contexts: { react: { componentStack: errorInfo.componentStack ?? '' } },
        tags: { location: window.location.pathname },
      });
    }).catch(() => { /* Sentry non disponible */ });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined });
  };

  private handleGoHome = () => {
    // Navigation SPA sans rechargement (compatible Cloudflare Pages)
    window.history.pushState({}, '', '/');
    window.dispatchEvent(new PopStateEvent('popstate'));
    this.setState({ hasError: false, error: undefined });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const isPetContextError = this.state.error?.message?.includes('usePetContext must be used within a PetProvider');

      return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background p-8">
          <img src="/octoveto-logo.webp" alt="Octoveto" className="w-16 mb-6" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          <h2 className="text-xl font-semibold mb-2">Oups, quelque chose s'est mal passé</h2>
          <p className="text-muted-foreground text-center mb-6 max-w-sm">
            {isPetContextError
              ? 'Un problème de configuration est survenu. Veuillez recharger la page.'
              : 'Une erreur inattendue s\'est produite. Notre équipe a été notifiée automatiquement.'}
          </p>
          <div className="flex gap-3">
            <Button onClick={this.handleReset} variant="default" size="sm">
              <RefreshCcw className="h-4 w-4 mr-2" />
              Réessayer
            </Button>
            <Button onClick={this.handleReload} variant="outline" size="sm">
              Recharger la page
            </Button>
            <Button onClick={this.handleGoHome} variant="ghost" size="sm">
              Retour à l'accueil
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;